import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  FiArrowLeft,
  FiCalendar,
  FiChevronLeft,
  FiClock,
  FiHome,
} from "react-icons/fi";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { NewsCard } from "../components/news/NewsCard";
import { getNewsDetailPath } from "../config/routes";
import { Footer } from "../components/landing/Footer";
import { Navbar } from "../components/landing/Navbar";
import { getNewsCategoryById } from "../data/news";
import { news } from "../services/api";
import {
  formatNewsDate,
  formatNewsTime,
  getNewsAuthorLabel,
  getNewsContentHtml,
  getNewsImage,
  getNewsSeoPathSegment,
} from "../utils/news";

export const NewsDetailPage = () => {
  const { category, slug, seoTitle } = useParams<{
    category: string;
    slug: string;
    seoTitle: string;
  }>();
  const navigate = useNavigate();
  const activeCategory = getNewsCategoryById(category);

  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "dark.50");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const articleBg = useColorModeValue("gray.50", "dark.100");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-news-detail", category, slug, seoTitle],
    queryFn: () => news.getPublicOne(category!, slug!, seoTitle!),
    enabled: Boolean(category && slug && seoTitle),
  });

  const item = data?.news;

  const { data: relatedData, isLoading: isLoadingRelated } = useQuery({
    queryKey: ["public-news-related", category],
    queryFn: () => news.getPublic({ category, pageSize: 4 }),
    enabled: Boolean(category),
  });

  const relatedItems =
    relatedData?.news
      ?.filter((entry: any) => entry.slug !== slug)
      .slice(0, 3) || [];
  const authorLabel = getNewsAuthorLabel(item?.author, item?.authorName);

  if (isLoading) {
    return (
      <Box dir="rtl" overflowX="hidden">
        <Navbar />
        <HStack justify="center" py={40}>
          <Spinner size="xl" />
        </HStack>
        <Footer />
      </Box>
    );
  }

  if (!item || !activeCategory || isError) {
    return (
      <Box dir="rtl" overflowX="hidden">
        <Navbar />
        <Box py={40}>
          <Container maxW="container.md">
            <VStack spacing={5} textAlign="center">
              <Heading color={textColor} fontSize="2xl">
                محتوای مورد نظر یافت نشد
              </Heading>
              <Text color={subtitleColor}>
                این مطلب ممکن است حذف شده باشد یا آدرس آن تغییر کرده باشد.
              </Text>
              <Button colorScheme="brand" onClick={() => navigate("/news")}>
                بازگشت به اطلاع‌رسانی
              </Button>
            </VStack>
          </Container>
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />
      <PageHero
        title={item.title}
        titleHighlight={activeCategory.title}
        subtitle={item.excerpt || "جزئیات این خبر را در ادامه مطالعه کنید."}
        primaryAction={{
          label: "بازگشت به دسته‌بندی",
          onClick: () => navigate(`/news/${activeCategory.id}`),
        }}
      />

      <SectionWrapper py={{ base: 10, md: 14 }}>
        <Container maxW="container.lg">
          <VStack spacing={8} align="stretch">
            <Breadcrumb
              separator={
                <Icon
                  verticalAlign="middle"
                  as={FiChevronLeft as any}
                  color={subtitleColor}
                />
              }
              fontSize="sm"
              color={subtitleColor}
            >
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">
                  <HStack spacing={1}>
                    <Icon
                      verticalAlign="middle"
                      as={FiHome as any}
                      boxSize={3.5}
                    />
                    <Text>خانه</Text>
                  </HStack>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/news">
                  اطلاع‌رسانی
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={`/news/${activeCategory.id}`}
                >
                  {activeCategory.title}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <Text color={textColor}>{item.title}</Text>
              </BreadcrumbItem>
            </Breadcrumb>

            <Box
              bg={cardBg}
              borderRadius="2xl"
              p={{ base: 5, md: 7 }}
              border="1px solid"
              borderColor={borderColor}
            >
              <VStack align="stretch" spacing={4}>
                <HStack spacing={3} flexWrap="wrap">
                  <Badge colorScheme={activeCategory.accentColor}>
                    {activeCategory.title}
                  </Badge>
                  {item.isFeatured ? (
                    <Badge variant="subtle">منتخب</Badge>
                  ) : null}
                  <HStack spacing={1} color={subtitleColor} fontSize="sm">
                    <Icon verticalAlign="middle" as={FiCalendar as any} />
                    <Text>{formatNewsDate(item.createdAt)}</Text>
                  </HStack>
                  {formatNewsTime(item.createdAt) ? (
                    <HStack spacing={1} color={subtitleColor} fontSize="sm">
                      <Icon verticalAlign="middle" as={FiClock as any} />
                      <Text>{formatNewsTime(item.createdAt)}</Text>
                    </HStack>
                  ) : null}
                  {authorLabel ? (
                    <Text color={subtitleColor} fontSize="sm">
                      {authorLabel}
                    </Text>
                  ) : null}
                </HStack>

                {item.tags?.length ? (
                  <HStack spacing={2} flexWrap="wrap">
                    {item.tags.map((tag: string) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </HStack>
                ) : null}
              </VStack>
            </Box>

            <Box overflow="hidden" borderRadius="3xl" aspectRatio={16 / 9}>
              <Image
                src={getNewsImage(item)}
                alt={item.title}
                w="100%"
                h="100%"
                objectFit="cover"
              />
            </Box>

            <Box
              bg={articleBg}
              borderRadius="3xl"
              p={{ base: 6, md: 10 }}
              border="1px solid"
              borderColor={borderColor}
            >
              <Box
                color={subtitleColor}
                fontSize={{ base: "sm", md: "md" }}
                lineHeight="2.2"
                sx={{
                  "& h1, & h2, & h3": {
                    color: textColor,
                    lineHeight: "1.8",
                    marginTop: "1.25rem",
                    marginBottom: "0.75rem",
                  },
                  "& h2": {
                    fontSize: "1.35rem",
                  },
                  "& h3": {
                    fontSize: "1.15rem",
                  },
                  "& p": {
                    marginBottom: "1rem",
                  },
                  "& ul, & ol": {
                    paddingRight: "1.5rem",
                    marginBottom: "1rem",
                  },
                  "& blockquote": {
                    borderRight: "3px solid",
                    borderColor: borderColor,
                    background: cardBg,
                    padding: "0.75rem 1rem",
                    margin: "1rem 0",
                    borderRadius: "0.75rem",
                  },
                  "& a": {
                    color: "blue.500",
                    textDecoration: "underline",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: getNewsContentHtml(item.content),
                }}
              />
            </Box>
          </VStack>
        </Container>
      </SectionWrapper>

      {!isLoadingRelated && relatedItems.length > 0 ? (
        <SectionWrapper alt>
          <Container maxW="container.xl">
            <VStack spacing={4} textAlign="center" mb={10}>
              <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }}>
                مطالب مرتبط
              </Heading>
              <Text color={subtitleColor} maxW="720px">
                چند خبر دیگر از همین دسته برای ادامه مطالعه در دسترس است.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {relatedItems.map((related: any) => (
                <NewsCard
                  key={related._id}
                  item={related}
                  onClick={() =>
                    navigate(
                      getNewsDetailPath(
                        related.category,
                        related.slug,
                        getNewsSeoPathSegment(related),
                      ),
                    )
                  }
                />
              ))}
            </SimpleGrid>

            <HStack justify="center" pt={8}>
              <Button
                colorScheme="brand"
                variant="secondary"
                rightIcon={
                  <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                }
                onClick={() => navigate(`/news/${activeCategory.id}`)}
              >
                مشاهده همه مطالب این دسته
              </Button>
            </HStack>
          </Container>
        </SectionWrapper>
      ) : null}

      <Footer />
    </Box>
  );
};
